import React from 'react'
import { DesignTokens, TailwindColor } from '../types'
import {
  COLOR_VALUES,
  COLOR_VALUES_DARK,
  COLOR_VALUES_LIGHT,
  COLOR_VALUES_BG,
  NEUTRAL_BG,
  NEUTRAL_TEXT,
  NEUTRAL_TEXT_SECONDARY,
  NEUTRAL_BORDER,
} from '../data/tailwindColors'
import { getFontFamilyValue, FONT_WEIGHT_VALUES, FONT_SIZE_VALUES, FONT_SIZE_LINE_HEIGHTS } from '../data/googleFonts'

const RADIUS_VALUES = {
  none: '0',
  sm: '0.125rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  full: '9999px',
}

const SHADOW_VALUES = {
  none: 'none',
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
}

// Convert DesignTokens to CSS variables for runtime injection
export function tokensToCssVariables(tokens: DesignTokens): React.CSSProperties {
  const primary = tokens.primaryColor
  const neutral = tokens.neutralColor

  const cssVars: Record<string, string> = {
    // Primary colors
    '--color-primary': COLOR_VALUES[primary],
    '--color-primary-light': COLOR_VALUES_LIGHT[primary],
    '--color-primary-dark': COLOR_VALUES_DARK[primary],
    '--color-primary-bg': COLOR_VALUES_BG[primary],

    // Neutral colors
    '--color-neutral-bg': NEUTRAL_BG[neutral],
    '--color-neutral-text': NEUTRAL_TEXT[neutral],
    '--color-neutral-text-secondary': NEUTRAL_TEXT_SECONDARY[neutral],
    '--color-neutral-border': NEUTRAL_BORDER[neutral],

    // Typography
    '--font-display': getFontFamilyValue(tokens.typography.displayFont.family),
    '--font-body': getFontFamilyValue(tokens.typography.bodyFont.family),
    '--font-mono': getFontFamilyValue(tokens.typography.monoFont.family),
    '--font-weight-display': String(FONT_WEIGHT_VALUES[tokens.typography.displayFont.weight]),
    '--font-weight-body': String(FONT_WEIGHT_VALUES[tokens.typography.bodyFont.weight]),
    '--font-size-display': FONT_SIZE_VALUES[tokens.typography.displayFont.size],
    '--font-size-body': FONT_SIZE_VALUES[tokens.typography.bodyFont.size],
    '--line-height-display': FONT_SIZE_LINE_HEIGHTS[tokens.typography.displayFont.size],
    '--line-height-body': FONT_SIZE_LINE_HEIGHTS[tokens.typography.bodyFont.size],

    // Layout
    '--radius': RADIUS_VALUES[tokens.borderRadius],
    '--shadow': SHADOW_VALUES[tokens.shadow],
  }

  // Secondary colors (optional)
  if (tokens.secondaryColor) {
    const secondary = tokens.secondaryColor as TailwindColor
    cssVars['--color-secondary'] = COLOR_VALUES[secondary]
    cssVars['--color-secondary-light'] = COLOR_VALUES_LIGHT[secondary]
    cssVars['--color-secondary-dark'] = COLOR_VALUES_DARK[secondary]
    cssVars['--color-secondary-bg'] = COLOR_VALUES_BG[secondary]
  }

  return cssVars as React.CSSProperties
}

// Get individual computed values (for components that need raw values)
export function getComputedTokenValues(tokens: DesignTokens) {
  const primary = tokens.primaryColor
  const neutral = tokens.neutralColor

  return {
    primary: COLOR_VALUES[primary],
    primaryLight: COLOR_VALUES_LIGHT[primary],
    primaryDark: COLOR_VALUES_DARK[primary],
    primaryBg: COLOR_VALUES_BG[primary],

    secondary: tokens.secondaryColor ? COLOR_VALUES[tokens.secondaryColor] : null,
    secondaryLight: tokens.secondaryColor ? COLOR_VALUES_LIGHT[tokens.secondaryColor] : null,
    secondaryDark: tokens.secondaryColor ? COLOR_VALUES_DARK[tokens.secondaryColor] : null,
    secondaryBg: tokens.secondaryColor ? COLOR_VALUES_BG[tokens.secondaryColor] : null,

    neutralBg: NEUTRAL_BG[neutral],
    neutralText: NEUTRAL_TEXT[neutral],
    neutralTextSecondary: NEUTRAL_TEXT_SECONDARY[neutral],
    neutralBorder: NEUTRAL_BORDER[neutral],

    fontDisplay: getFontFamilyValue(tokens.typography.displayFont.family),
    fontBody: getFontFamilyValue(tokens.typography.bodyFont.family),
    fontMono: getFontFamilyValue(tokens.typography.monoFont.family),
    fontWeightDisplay: FONT_WEIGHT_VALUES[tokens.typography.displayFont.weight],
    fontWeightBody: FONT_WEIGHT_VALUES[tokens.typography.bodyFont.weight],
    fontSizeDisplay: FONT_SIZE_VALUES[tokens.typography.displayFont.size],
    fontSizeBody: FONT_SIZE_VALUES[tokens.typography.bodyFont.size],
    lineHeightDisplay: FONT_SIZE_LINE_HEIGHTS[tokens.typography.displayFont.size],
    lineHeightBody: FONT_SIZE_LINE_HEIGHTS[tokens.typography.bodyFont.size],

    radius: RADIUS_VALUES[tokens.borderRadius],
    shadow: SHADOW_VALUES[tokens.shadow],
  }
}

export { RADIUS_VALUES, SHADOW_VALUES }
